import { motion } from 'framer-motion'

// Link da Hotmart para a Jornada Completa — substituir pelo link definitivo quando fornecido
const JORNADA_COMPLETA_LINK = 'https://pay.hotmart.com/O105657366F?off=x4c01y5z'

interface Props {
  accentSoft?: string
  priceLine2?: React.ReactNode
  hideBonus?: boolean
}

const CICLOS = [
  { icon: '🌱', title: 'Ciclo 1 — Raízes', desc: 'Liberação de programações familiares e crenças herdadas.' },
  { icon: '🌿', title: 'Ciclo 2 — Caule', desc: 'Interrupção de loops de autossabotagem e repetição.' },
  { icon: '🌾', title: 'Ciclo 3 — Ramos', desc: 'Liberação do não resolvido e ressignificação de memórias.' },
  { icon: '🍃', title: 'Ciclo 4 — Reconexão', desc: 'Liberte-se da identidade que o passado construiu e reconecte com quem você veio ser.' },
  { icon: '🌸', title: 'Ciclo 5 — Merecimento', desc: 'Dissolva a crença de que não merece e ancore uma nova realidade de plenitude e abundância.' },
  { icon: '🍎', title: 'Ciclo 6 — Crescimento', desc: 'Sustente sua transformação, expanda em todas as áreas e floresça de forma permanente.' },
]

const RECEBE = [
  'Diagnóstico atualizado do elemento mais bloqueado naquele ciclo.',
  'Mapa de Revelações personalizado com os padrões daquele ciclo.',
  'Trindade de Cura personalizada.',
  'Decretos do Eu Sou específicos para aquele ciclo.',
  'Meditação gravada com intenção direcionada para o seu campo para 21 noites.',
]

const BONUS = [
  { icon: '🌀', title: 'Coloque sua Coroa', striked: 'R$97,00' },
  { icon: '🎓', title: 'Acesso Completo ao Programa Eleve e Prospere', striked: 'R$297,00' },
  { icon: '🌍', title: 'Guia de Consulta dos 4 Elementos', striked: 'R$47,00' },
  { icon: '🌿', title: 'Protocolo dos 4 Elementos com Óleos Essenciais', striked: 'R$47,00' },
]

function JornadaCompletaOffer({ accentSoft = '#D4C3EA', priceLine2, hideBonus = false }: Props) {
  return (
    <div>
      {/* Separador visual entre Ciclo 1 e a Jornada Completa */}
      <div className="flex items-center justify-center gap-4 my-8 md:my-10">
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}55)` }} />
        <span
          className="uppercase font-body whitespace-nowrap"
          style={{ fontSize: '0.85rem', letterSpacing: '0.3em', color: accentSoft, opacity: 0.85 }}
        >
          ou vá mais fundo
        </span>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${accentSoft}55, transparent)` }} />
      </div>

      {/* Card da Jornada Completa */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="rounded-sm p-5 sm:p-6 md:p-10 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(200,146,58,0.06) 0%, rgba(255,255,255,0.01) 100%)',
          border: '1px solid rgba(200,146,58,0.35)',
        }}
      >
        {/* Top glow dourado */}
        <div
          className="absolute inset-x-0 -top-32 h-64 pointer-events-none"
          style={{ background: 'radial-gradient(closest-side, rgba(200,146,58,0.16), transparent)' }}
        />

        {/* Badge */}
        <div className="flex items-center gap-3 mb-7">
          <span
            className="uppercase font-body px-3 py-1 rounded-full"
            style={{
              fontSize: '0.78rem',
              letterSpacing: '0.3em',
              color: '#E8C77A',
              border: '1px solid rgba(200,146,58,0.45)',
              background: 'rgba(200,146,58,0.10)',
              lineHeight: 1.7,
            }}
          >
            Comece sua jornada de transformação.
          </span>
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(200,146,58,0.45), transparent)' }} />
        </div>

        {/* Título */}
        <h2
          className="font-display text-[var(--ink,#e8e0d5)] mb-3"
          style={{ fontSize: 'clamp(1.6rem, 4vw, 2rem)', fontWeight: 500, lineHeight: 1.18 }}
        >
          Jornada Completa de{' '}
          <em className="italic font-light" style={{ color: '#E8C77A' }}>
            Liberação e Cocriação
          </em>
        </h2>

        {/* Subtítulo */}
        <p
          className="font-body mb-6"
          style={{ fontSize: 'clamp(1.1rem, 2.4vw, 1.25rem)', fontWeight: 500, lineHeight: 1.5, color: '#E8C77A' }}
        >
          <span className="numerals">6</span> Ciclos de <span className="numerals">21</span> dias — <span className="numerals">126</span> dias de transformação progressiva
        </p>

        {/* Texto de apresentação */}
        <p className="font-body type-body mb-8" style={{ color: 'rgba(232,224,213,0.7)', lineHeight: 1.8 }}>
          A transformação real acontece em camadas. Cada ciclo dissolve uma camada e prepara o campo para a próxima. São <span className="numerals">126</span> dias de trabalho progressivo e personalizado — do enraizamento até o florescimento completo.
        </p>

        {/* Os 6 ciclos da jornada */}
        <p
          className="font-body mb-5"
          style={{ fontSize: 'clamp(1.2rem, 2.6vw, 1.35rem)', fontWeight: 600, lineHeight: 1.6, color: 'var(--ink, #e8e0d5)' }}
        >
          Os <span className="numerals">6</span> ciclos da jornada:
        </p>
        <ul className="space-y-4 mb-8">
          {CICLOS.map((c, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex-shrink-0" style={{ fontSize: '1.4rem', lineHeight: 1.4 }}>{c.icon}</span>
              <p className="font-body" style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', lineHeight: 1.6, color: 'rgba(232,224,213,0.8)' }}>
                <span style={{ color: 'var(--ink, #e8e0d5)', fontWeight: 500 }}>{c.title}</span> — {c.desc}
              </p>
            </li>
          ))}
        </ul>

        {/* O que você recebe em cada ciclo */}
        <p
          className="font-body mb-5"
          style={{ fontSize: 'clamp(1.2rem, 2.6vw, 1.35rem)', fontWeight: 600, lineHeight: 1.6, color: 'var(--ink, #e8e0d5)' }}
        >
          O que você recebe em cada ciclo:
        </p>
        <ul className="space-y-3 mb-8">
          {RECEBE.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex-shrink-0" style={{ color: '#E8C77A', fontSize: '1.1rem', lineHeight: 1.6 }}>✦</span>
              <span className="font-body" style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', lineHeight: 1.7, color: 'rgba(232,224,213,0.8)' }}>
                {item}
              </span>
            </li>
          ))}
        </ul>

        {/* Destaque — gravação humana, sem IA */}
        <div
          className="relative rounded-sm p-5 sm:p-6 md:p-7 mb-8"
          style={{
            background: 'linear-gradient(135deg, rgba(200,146,58,0.10) 0%, rgba(200,146,58,0.04) 100%)',
            border: '1px solid rgba(200,146,58,0.35)',
          }}
        >
          <div className="absolute left-0 inset-y-0 w-[3px] rounded-l-sm" style={{ background: 'linear-gradient(180deg, #E8C77A, #C8923A, transparent)' }} />
          <p className="uppercase font-body mb-3" style={{ fontSize: '0.78rem', letterSpacing: '0.3em', color: '#E8C77A', lineHeight: 1.6 }}>
            ✦ Gravado com presença real
          </p>
          <p className="font-body" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'var(--ink, #e8e0d5)' }}>
            Cada meditação é gravada por mim com presença e intenção direcionada especificamente para o seu campo. Nenhuma inteligência artificial é usada na gravação.
          </p>
        </div>

        {hideBonus ? null : (
          <div className="mb-8">
            <p className="font-body mb-5" style={{ fontSize: 'clamp(1.2rem, 2.6vw, 1.35rem)', fontWeight: 600, lineHeight: 1.6, color: '#E8C77A' }}>
              🎁 Bônus incluídos na Jornada Completa:
            </p>
            <ul className="space-y-4">
              {BONUS.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0" style={{ fontSize: '1.4rem', lineHeight: 1.5 }}>{b.icon}</span>
                  <p className="font-body" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 500, lineHeight: 1.5, color: 'var(--ink, #e8e0d5)' }}>
                    {b.title}{' '}
                    <span
                      className="numerals"
                      style={{ textDecoration: 'line-through', color: 'rgba(232,224,213,0.45)', fontWeight: 400, fontSize: '0.92em', marginLeft: '0.4em' }}
                    >
                      {b.striked}
                    </span>
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className="space-y-3 mb-5">
          {[
            'Diagnóstico do elemento mais bloqueado',
            'Mapa de Revelações personalizado',
            'Trindade de Cura personalizada',
            'Decretos Eu Sou de cada ciclo',
            'Meditação gravada com presença real para 21 noites',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="flex-shrink-0" style={{ color: '#E8C77A', fontSize: '1.1rem', lineHeight: 1.6 }}>✦</span>
              <span className="font-body" style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', lineHeight: 1.7, color: 'rgba(232,224,213,0.8)' }}>
                {item}
              </span>
            </li>
          ))}
        </ul>
        <p
          className="font-body mb-8"
          style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.78)' }}
        >
          <span className="numerals">6</span> ciclos progressivos de <span className="numerals">21</span> dias. São <span className="numerals">126</span> dias de transformação camada por camada.
        </p>

        {/* Bloco de valor — destaque dourado */}
        <div
          className="rounded-sm p-5 sm:p-6 mb-8 text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(200,146,58,0.14) 0%, rgba(200,146,58,0.04) 100%)',
            border: '1px solid rgba(200,146,58,0.5)',
          }}
        >
          {priceLine2 ?? (
            <>
              <p className="font-body mb-2" style={{ fontSize: 'clamp(1.35rem, 3.2vw, 1.7rem)', fontWeight: 600, lineHeight: 1.3, color: '#F0D08A' }}>
                De{' '}
                <span className="numerals" style={{ textDecoration: 'line-through', color: 'rgba(232,224,213,0.45)', fontWeight: 400 }}>R$1.782</span>
                {' '}por{' '}
                <span className="numerals">R$997</span>
                {' '}à vista
              </p>
              <p className="font-body" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.4, color: 'rgba(232,224,213,0.75)' }}>
                ou <span className="numerals">12x</span> de <span className="numerals">R$103,11</span>
              </p>
            </>
          )}
        </div>

        {/* Texto abaixo do valor + Botão */}
        <div className="pt-2">
          <a
            href={JORNADA_COMPLETA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center rounded-xl uppercase transition-all duration-300 hover:brightness-110 hover:-translate-y-[1px] active:translate-y-0"
            style={{
              fontSize: 'clamp(1.2rem, 2.6vw, 1.35rem)',
              fontWeight: 600,
              letterSpacing: '0.04em',
              padding: '20px 28px',
              minHeight: '60px',
              lineHeight: 1.4,
              background: '#C8923A',
              color: '#ffffff',
              boxShadow: '0 6px 18px rgba(200,146,58,0.28), 0 1px 2px rgba(0,0,0,0.18)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            Quero a Jornada Completa
          </a>
          <p
            className="font-body text-center mt-4"
            style={{ fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.6, color: 'rgba(232,224,213,0.55)', fontStyle: 'italic' }}
          >
            Os ciclos são entregues progressivamente — um a cada <span className="numerals">21</span> dias conforme você avança na jornada.
          </p>
        </div>
      </motion.div>
    </div>
  )
}

export default JornadaCompletaOffer
